import apiClient, { type RequestConfig } from './client'
import type { ExportDestination } from './types'

const ENDPOINT = '/handoff'

export interface HandoffStepResponse {
  status: string
  record_id?: string | null
  created_at?: string | null
  completed_at?: string | null
  is_stale?: boolean
  stale_reason?: string | null
  detail: Record<string, unknown>
}

export interface HandoffAssetResponse extends HandoffStepResponse {
  format?: string | null
  download_url?: string | null
  preview_url?: string | null
  detail: Record<string, unknown> & {
    manifest_present?: boolean
    manifest_sha256?: string | null
    verified_at?: string | null
    intake_manifest_present?: boolean
    extraction_manifest_present?: boolean
  }
}

export interface HandoffStatusResponse {
  handoff_id: string
  matter_id: string
  overall_status: string
  freshness_status: string
  last_corpus_change_at?: string | null
  package_eligible: boolean
  package_ready: boolean
  effective_package_eligible: boolean
  effective_package_ready: boolean
  override_allowed: boolean
  override_active: boolean
  override_reason?: string | null
  override_recorded_at?: string | null
  overridable_blockers: string[]
  blockers: string[]
  recommended_actions: string[]
  stale_reasons: string[]
  reduction_summary: Record<string, unknown>
  collection_gap_summary: Record<string, unknown>
  preflight: HandoffStepResponse
  sweep: HandoffStepResponse
  chronology: HandoffStepResponse
  export: HandoffAssetResponse
  report_pack: HandoffAssetResponse
  proof_automation?: {
    eligible: boolean
    enabled: boolean
    latest_run_id?: string | null
    latest_status?: string | null
    next_action: string
    last_completed_at?: string | null
    doc_count?: number
    sweep_doc_limit?: number
    large_matter?: boolean
    sweep_scope_required?: boolean
    proof_mode?: string
    latest_actions?: string[]
    latest_blockers?: string[]
    sweep_target?: Record<string, unknown> | null
    skip_reason?: string | null
    evidence_status?: string
    outputs_status?: string
    spend_gate?: string
  }
}

export interface HandoffStartRequest {
  matter_id: string
  sweep_fidelity?: 'A' | 'B' | 'C'
  chronology_max_docs?: number
  export_format?: ExportDestination
  include_privileged?: boolean
  include_natives?: boolean
  relevance_threshold?: number
  rerun_preflight?: boolean
  rerun_sweep?: boolean
  rerun_chronology?: boolean
  requeue_export?: boolean
  run_preflight?: boolean
  run_sweep?: boolean
  run_chronology?: boolean
  queue_export?: boolean
  generate_report_pack?: boolean
  override_blockers?: boolean
  override_reason?: string
  downstream_doc_rate?: number
}

export interface HandoffStartResponse extends HandoffStatusResponse {
  actions_taken: string[]
}

export interface HandoffStatusQuery {
  export_format?: ExportDestination
  report_tier?: string
  legalese?: string
  downstream_doc_rate?: number
}

export interface HandoffPackageQuery extends HandoffStatusQuery {
  build_report_pack?: boolean
  override_blockers?: boolean
  override_reason?: string
}

export const handoffApi = {
  async start(
    request: HandoffStartRequest,
    config?: RequestConfig
  ): Promise<HandoffStartResponse> {
    const { data } = await apiClient.post<HandoffStartResponse>(`${ENDPOINT}/start`, request, config)
    return data
  },

  async getStatus(
    handoffId: string,
    query?: HandoffStatusQuery,
    config?: RequestConfig
  ): Promise<HandoffStatusResponse> {
    const { data } = await apiClient.get<HandoffStatusResponse>(`${ENDPOINT}/${handoffId}`, {
      ...config,
      params: query as Record<string, string | number | boolean | undefined> | undefined,
    })
    return data
  },

  async getPackage(
    handoffId: string,
    query?: HandoffPackageQuery,
    config?: RequestConfig
  ): Promise<HandoffStatusResponse> {
    const { data } = await apiClient.get<HandoffStatusResponse>(`${ENDPOINT}/${handoffId}/package`, {
      ...config,
      params: query as Record<string, string | number | boolean | undefined> | undefined,
    })
    return data
  },
}
